import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { ConceptInvoicingImportModel } from 'src/app/models/imports/concept-invoicing-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from '../../../environments/environment';
import { ConceptInvoicingModel } from '../../models/invoicing/concept-invoicing.model';

@Injectable({
  providedIn: 'root'
})
export class ConceptInvoicingService {

  constructor(private _httpClient: HttpClient) { }

  createConceptInvoicing(conceptContractModel: ConceptInvoicingModel): Observable<ConceptInvoicingModel> {
    return this._httpClient.post<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.CreateConceptInvoicing}`,
      conceptContractModel);
  }

  createPurchaseConcept(conceptContractModel: ConceptInvoicingModel): Observable<ConceptInvoicingModel> {
    return this._httpClient.post<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.CreatePurchaseConcept}`,
      conceptContractModel);
  }

  deleteConceptInvoicing(conceptId: number): Observable<ConceptInvoicingModel> {
    return this._httpClient.delete<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.DeleteConceptInvoicing}/${conceptId}`);
  }

  deletePurchaseConcept(conceptId: number): Observable<ConceptInvoicingModel> {
    return this._httpClient.delete<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.DeletePurchaseConcept}/${conceptId}`);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  downloadPurchaseGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.DownloadPurchaseGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.ExportImportErrors}`, alerts,
      { responseType: 'blob' }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportPurchaseGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.ExportPurchaseGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  exportGeneralConsultationForAccountingModel(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.ExportGeneralConsultationForAccountingModel}`, filters, {
      params, responseType: 'blob',
    });
  }

  getAllConceptInvoicingsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllConceptInvoicingsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllPurchaseConceptInvoicingsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllPurchaseConceptInvoicingsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllConceptInvoicingsForAccountingModelWithPaginationAsync(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllConceptInvoicingsForAccountingModelWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllConceptInvoicingPriceOfsForSelect(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllConceptInvoicingPriceOfsForSelectAsync}`);
  }

  getAllConceptsInvoicingForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllConceptsInvoicingForSelectAsync}`);
  }

  getAllPurchaseConceptsForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllPurchaseConceptsForSelectAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForPurchaseConceptGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetColumnsForPurchaseConceptGeneralConsultationAsync}`);
  }

  getColumnsForGeneralConsultationForAccountingModelAsync(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetColumnsForGeneralConsultationForAccountingModelAsync}`);
  }

  getConceptInvoicing(conceptInvoicingIds: number[]): Observable<Array<ItemFilterModel>> {
    return this._httpClient.post<Array<ItemFilterModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicing}`,
      conceptInvoicingIds);
  }

  getConceptInvoicingAlquiler(): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicingAlquiler}`);
  }

  getConceptInvoicingByCode(code: number): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicingByCode}/${code}`)
  }

  getConceptInvoicingByCompanyId(companyId: number): Observable<Array<ConceptInvoicingModel>> {
    return this._httpClient.get<Array<ConceptInvoicingModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicingByCompanyId}/${companyId}`);
  }

  getAllConceptInvoicingByCompanyForSelectAsync(companyId: number): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetAllConceptInvoicingByCompanyForSelectAsync}/${companyId}`,);
  }

  getConceptInvoicingById(conceptId: number): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicingById}/${conceptId}`)
  }

  getConceptInvoicingForNote(invoiceId: number): Observable<Array<ConceptInvoicingModel>> {
    return this._httpClient.get<Array<ConceptInvoicingModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetConceptInvoicingForNote}/${invoiceId}`);
  }

  getFirstConceptsInvoicingByKeys(): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetFirstConceptsInvoicingByKeysAsync}`);
  }

  getFirstPurchaseConceptByKeys(): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetFirstPurchaseConceptByKeysAsync}`);
  }

  getLastConceptsInvoicingByKeys(): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetLastConceptsInvoicingByKeysAsync}`);
  }

  getLastPurchaseConceptByKeys(): Observable<ConceptInvoicingModel> {
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetLastPurchaseConceptByKeysAsync}`);
  }

  getNextConceptsInvoicingByKeys(code: number): Observable<ConceptInvoicingModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetNextConceptsInvoicingByKeysAsync}`,
      { params: params }
    );
  }

  getNextPurchaseConceptByKeys(code: number): Observable<ConceptInvoicingModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetNextPurchaseConceptByKeysAsync}`,
      { params: params }
    );
  }

  getPreviusConceptsInvoicingByKeys(code: number): Observable<ConceptInvoicingModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetPreviusConceptsInvoicingByKeysAsync}`,
      { params: params }
    );
  }

  getPreviusPurchaseConceptByKeys(code: number): Observable<ConceptInvoicingModel> {
    const params = new HttpParams().set('code', code);
    return this._httpClient.get<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.GetPreviusPurchaseConceptByKeysAsync}`,
      { params: params }
    );
  }

  importConceptInvoicing(conceptInvoicingImports: Array<ConceptInvoicingImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.ImportConceptInvoicing}`,
      conceptInvoicingImports
    );
  }

  updateConceptInvoicing(conceptInvoicingModel: ConceptInvoicingModel): Observable<ConceptInvoicingModel> {
    return this._httpClient.put<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.UpdateConceptInvoicing}`,
      conceptInvoicingModel);
  }

  updatePurchaseConcept(conceptInvoicingModel: ConceptInvoicingModel): Observable<ConceptInvoicingModel> {
    return this._httpClient.put<ConceptInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.conceptInvoicing.UpdatePurchaseConcept}`,
      conceptInvoicingModel);
  }
}
